import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Avatar, Badge, Button, Card, HStack, Icon, Separator, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { useAdvisorsCollectionLoadMore } from '@src/components/sco/Advisors/hooks/useAdvisorsCollectionLoadMore';
import { useOpportunityCounts } from '@src/components/sco/Advisors/hooks/useOpportunityCounts';
import { OpportunitiesAccessGate, OpportunitiesLayout } from '@src/components/sco/Advisors/layout/OpportunitiesLayout';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { openSeatProfileHref } from '@src/components/sco/Advisors/utils/openSeatMatches';
import { formatWhen, personName } from '@src/components/sco/Advisors/utils/sessionFormat';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_SEAT_APPLICATIONS } from '@src/gql/queries/AdvisorOpportunities';
import { GroupCommitment, GroupCommitmentCollection } from '@src/graphql-types';
import { LuClock } from 'react-icons/lu';
const PAGE_SIZE = 20;
type ApplicationsData = {
  advisorSeatApplications?: GroupCommitmentCollection | null;
};
type ApplicationBadge = {
  label: string;
  colorPalette: string;
};
const applicationBadge = (item: GroupCommitment): ApplicationBadge => {
  if (item.rejectedAt) {
    return {
      label: 'Declined',
      colorPalette: 'red'
    };
  }
  if (item.acceptedAt) {
    return {
      label: 'Accepted',
      colorPalette: 'green'
    };
  }
  return {
    label: 'Pending',
    colorPalette: 'blue'
  };
};
const ApplicationsBody = () => {
  const $ = _c(19);
  const paths = useAdvisorsPaths();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      variables: {
        pageSize: PAGE_SIZE,
        page: 1
      },
      fetchPolicy: "network-only"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const query = useQuery(ADVISOR_SEAT_APPLICATIONS, t0);
  let t1;
  if ($[1] !== query.data?.advisorSeatApplications?.items) {
    t1 = query.data?.advisorSeatApplications?.items ?? [];
    $[1] = query.data?.advisorSeatApplications?.items;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = t1.filter(_temp);
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const firstPageApplications = t2;
  const t3 = query.data?.advisorSeatApplications?.pagination?.hasNextPage;
  let t4;
  if ($[5] !== firstPageApplications || $[6] !== query.fetchMore || $[7] !== t3) {
    t4 = {
      fetchMore: query.fetchMore,
      field: "advisorSeatApplications",
      firstPageItems: firstPageApplications,
      hasNextPage: t3
    };
    $[5] = firstPageApplications;
    $[6] = query.fetchMore;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    items: applications,
    loadMore,
    loadingMore,
    hasNextPage
  } = useAdvisorsCollectionLoadMore(t4);
  const t5 = query.loading && !query.data;
  let t6;
  if ($[9] !== applications || $[10] !== hasNextPage || $[11] !== loadMore || $[12] !== loadingMore || $[13] !== paths) {
    t6 = applications.length === 0 ? <Card.Root variant="subtle"><Card.Body><VStack align="stretch" gap={3}><Text color="colorPalette.fg" fontWeight="semibold">You haven't applied to any board seats yet.</Text><Text color="colorPalette.fg/50" fontSize="xs">When you apply to an open seat, it lands here until the founder accepts or declines.</Text><Button asChild={true} alignSelf="flex-start" size="sm" variant="cta"><NextLink href={paths.opportunityMatches}>Browse matched seats</NextLink></Button></VStack></Card.Body></Card.Root> : <VStack align="stretch" gap={3}><SimpleGrid columns={{
        base: 1,
        sm: 2,
        md: 3
      }} gap={3}>{applications.map(application => {
          const founder = application.groupRequest?.requestingUser;
          const founderAvatar = application.groupRequest?.requestingUser?.pictureUrl;
          const companyName = application.groupRequest?.requestingCompany?.name;
          const seatTitle = application.groupRequest?.title || "Board seat";
          const userMatchId = application.groupRequest?.currentUserOpenSeatMatch?.userMatchId;
          const seatHref = userMatchId ? openSeatProfileHref(userMatchId, paths.base) : null;
          const inboxHref = founder?.userId ? paths.withQuery(paths.inbox, {
            founderUserId: founder.userId
          }) : null;
          const badge = applicationBadge(application);
          const appliedAt = application.submittedAt || application.createdAt;
          return <Card.Root key={application.groupCommitmentId} variant="subtle"><Card.Body gap={4}><HStack align="start" justify="space-between" wrap="wrap"><VStack align="start" gap={4}><HStack align="center" gap={2}><Avatar.Root size="lg"><Avatar.Image src={founderAvatar} /><Avatar.Fallback>{personName(founder, "Founder").charAt(0)}</Avatar.Fallback></Avatar.Root><VStack align="start" gap={0}><Text fontWeight="semibold">{personName(founder, "Founder")}</Text><Text color="colorPalette.fg/60" fontSize="sm">{[companyName, seatTitle].filter(Boolean).join(" \xB7 ")}</Text></VStack></HStack>{appliedAt && <HStack align="center" gap={2}><Icon as={LuClock} boxSize={4} color="colorPalette.fg/50" /><Text color="colorPalette.fg/50" fontSize="xs">Applied {formatWhen(appliedAt)}</Text></HStack>}{application.bodyAsMarkdown && <Text color="colorPalette.fg/70" fontFamily="longform" fontSize="sm" lineClamp={3} marginTop={1}>{application.bodyAsMarkdown}</Text>}</VStack><Badge colorPalette={badge.colorPalette} variant="subtle">{badge.label}</Badge></HStack><Separator /><HStack gap={2} wrap="wrap">{seatHref && <Button asChild={true} size="sm" variant="cta"><NextLink href={seatHref}>View seat</NextLink></Button>}{application.acceptedAt && inboxHref && <Button asChild={true} size="sm" variant={seatHref ? "outline" : "cta"}><NextLink href={inboxHref}>Open in Inbox</NextLink></Button>}{founder?.slug && <Button asChild={true} size="sm" variant="ghost"><NextLink href={`/members/profile/${founder.slug}`}>View founder</NextLink></Button>}</HStack></Card.Body></Card.Root>;
        })}</SimpleGrid><AdvisorsLoadMore hasNextPage={hasNextPage} loading={loadingMore} onLoadMore={loadMore} /></VStack>;
    $[9] = applications;
    $[10] = hasNextPage;
    $[11] = loadMore;
    $[12] = loadingMore;
    $[13] = paths;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== query.error || $[16] !== t5 || $[17] !== t6) {
    t7 = <AdvisorsQueryState error={query.error} loading={t5}>{t6}</AdvisorsQueryState>;
    $[15] = query.error;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
};

/** `/advisors/opportunities/applications` — seats this advisor has applied to. */
export const OpportunitiesApplicationsSection = () => {
  const $ = _c(3);
  const {
    counts
  } = useOpportunityCounts();
  const t0 = counts.applied > 0 ? "Open seats you applied to. Founders accept or decline from their seat." : "Open seats you apply to land here until the founder responds.";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ApplicationsBody />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t0) {
    t2 = <OpportunitiesAccessGate><OpportunitiesLayout activeKey="applications" title="Board applications" description={t0}>{t1}</OpportunitiesLayout></OpportunitiesAccessGate>;
    $[1] = t0;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
function _temp(item) {
  return !!item?.groupCommitmentId;
}